import {
  Button,
  ButtonGroup,
  ButtonGroupProps,
  Center,
  Menu,
  MenuButton,
  MenuList,
  useMultiStyleConfig,
} from "@chakra-ui/react";
import React, { useRef } from "react";
import { MenuPortal } from "shared/components/menu/MenuPortal";
import { useElementRect } from "shared/hooks/useElementRect";
import { SmallArrowDownIcon } from "shared/icons/SmallArrowDownIcon";
import { useWindowSize } from "usehooks-ts";

export interface DropdownButtonProps extends ButtonGroupProps {
  title: string;
  onClick?: () => void;
}

export const DropdownButton: React.FC<
  React.PropsWithChildren<DropdownButtonProps>
> = ({
  title,
  onClick,
  children,
  isDisabled,
  variant,
  colorScheme,
  size,
  ...otherProps
}) => {
  const windowSize = useWindowSize();

  const buttonRef = useRef<HTMLButtonElement>();
  const buttonRect = useElementRect(buttonRef);
  const hasChildren = React.Children.count(children) > 0;

  const maxHeight = Math.max(
    windowSize.height - buttonRect.top - buttonRect.height - 16,
    400,
  );

  const menuList = (
    <MenuPortal>
      <MenuList minWidth={buttonRect.width} maxHeight={maxHeight}>
        {children}
      </MenuList>
    </MenuPortal>
  );

  const styles = useMultiStyleConfig("DropdownButton", {
    variant,
    colorScheme,
    size,
  });

  if (!onClick) {
    return (
      <Menu
        offset={[0, 0]}
        placement="bottom-end"
        isLazy={true}
        lazyBehavior="unmount"
      >
        <MenuButton
          ref={buttonRef}
          as={Button}
          rightIcon={<SmallArrowDownIcon className="mb-icon" />}
          isDisabled={isDisabled || !hasChildren}
          variant={variant}
          colorScheme={colorScheme}
          size={size}
          sx={styles.button}
        >
          {title}
        </MenuButton>
        {menuList}
      </Menu>
    );
  }

  return (
    <ButtonGroup
      data-ls-enabled={true}
      data-public={true}
      isAttached={true}
      variant={variant}
      colorScheme={colorScheme}
      size={size}
      isDisabled={isDisabled}
      {...otherProps}
    >
      <Menu
        offset={[0, 4]}
        placement="bottom-end"
        isLazy={true}
        lazyBehavior="unmount"
      >
        <Button onClick={onClick} sx={styles.text}>
          {title}
        </Button>
        <MenuButton
          as={Button}
          isDisabled={isDisabled || !hasChildren}
          sx={{
            ...styles.iconContainer,
            ...styles.button,
          }}
        >
          <Center w="16px" h="16px" display="inline-flex">
            <SmallArrowDownIcon className="mb-icon" />
          </Center>
        </MenuButton>
        {menuList}
      </Menu>
    </ButtonGroup>
  );
};
